import type { FilterSpecMap } from '@/hooks/use-index-filters';
import type { Labelled, ResourceTableDescriptor, TranslateFn } from './types';

/** Resolve a descriptor's translation key against the current locale. */
export function label(t: TranslateFn, item: Labelled): string {
    return t(item.labelKey, item.labelFallback);
}

/** Where a descriptor is being rendered, and what that place cannot show. */
export interface DerivationScope {
    /** Columns not applicable here — e.g. `company` on a company's own detail page. */
    hiddenColumnIds?: readonly string[];
    /** Filters not applicable here. The card and the predicate both disappear. */
    hiddenFilterIds?: readonly string[];
    /**
     * Restrict sort entries to columns the server can actually order by. Index
     * pages set this; embedded tables sort in JS and do not.
     */
    requireServerSort?: boolean;
}

export interface ToolbarDefinitions {
    columnDefinitions: Array<{ id: string; label: string }>;
    quickFilterDefinitions: Array<{ id: string; label: string }>;
    extraFilterDefinitions: Array<{ id: string; label: string }>;
    sortableColumns: Array<{ id: string; label: string }>;
    excludeFromMoreFilters: string[];
    showMoreFilters: boolean;
    allColumnIds: string[];
    defaultVisibleColumns: string[];
    compactColumnIds: string[];
}

/**
 * Everything GenericToolbar needs, derived from the descriptor once.
 *
 * Index pages and embedded tables both call this, which is what stops their
 * column pickers, filter cards and sort menus from drifting apart.
 */
export function buildToolbarDefinitions<TRow>(
    descriptor: ResourceTableDescriptor<TRow>,
    t: TranslateFn,
    scope: DerivationScope = {},
): ToolbarDefinitions {
    const hiddenColumns = new Set(scope.hiddenColumnIds ?? []);
    const hiddenFilters = new Set(scope.hiddenFilterIds ?? []);

    const columns = descriptor.columns.filter((column) => !hiddenColumns.has(column.id));
    const filters = descriptor.filters.filter((filter) => !hiddenFilters.has(filter.id));
    const columnIds = new Set(columns.map((column) => column.id));
    const quickFilterIds = descriptor.quickFilterIds.filter((id) => !hiddenFilters.has(id));

    // Quick filters follow quickFilterIds order, which is the order the modal lays
    // its cards out in — not the order the filters happen to be declared.
    const quickFilters = quickFilterIds
        .map((id) => filters.find((filter) => filter.id === id))
        .filter((filter): filter is (typeof filters)[number] => filter !== undefined);

    return {
        columnDefinitions: columns.map((column) => ({ id: column.id, label: label(t, column) })),
        quickFilterDefinitions: quickFilters.map((filter) => ({ id: filter.id, label: label(t, filter) })),
        // Filters with no matching column and no quick-filter slot. GenericToolbar
        // wants these separate: columnDefinitions also drives the show/hide-columns
        // menu, where a filter-only id would appear as a phantom column.
        extraFilterDefinitions: filters
            .filter((filter) => !columnIds.has(filter.id) && !quickFilterIds.includes(filter.id))
            .map((filter) => ({ id: filter.id, label: label(t, filter) })),
        sortableColumns: columns
            .filter((column) => (scope.requireServerSort ? Boolean(column.sort?.serverKey) : Boolean(column.sort?.value)))
            .map((column) => ({ id: column.id, label: label(t, column) })),
        excludeFromMoreFilters: [...(descriptor.excludeFromMoreFilters ?? [])],
        showMoreFilters: descriptor.showMoreFilters ?? true,
        allColumnIds: columns.map((column) => column.id),
        defaultVisibleColumns: descriptor.defaultVisibleColumns.filter((id) => columnIds.has(id)),
        compactColumnIds: columns.filter((column) => column.compact).map((column) => column.id),
    };
}

/** The server half of the filter contract, for useIndexFilters. */
export function toFilterSpecMap<TRow>(
    descriptor: ResourceTableDescriptor<TRow>,
    scope: Pick<DerivationScope, 'hiddenFilterIds'> = {},
): FilterSpecMap {
    const hidden = new Set(scope.hiddenFilterIds ?? []);

    return Object.fromEntries(
        descriptor.filters
            .filter((filter) => !hidden.has(filter.id))
            .map((filter) => [
                filter.id,
                {
                    param: filter.param,
                    toQuery: filter.toQuery,
                    extraParams: filter.extraParams,
                    fromQuery: filter.fromQuery,
                    defaultValues: filter.defaultValues,
                },
            ]),
    );
}

/**
 * The `sort_by` value for a column, replacing the hand-maintained `sortByMap`
 * each index page used to carry.
 */
export function serverSortKey<TRow>(descriptor: ResourceTableDescriptor<TRow>, columnId: string): string | undefined {
    return descriptor.columns.find((column) => column.id === columnId)?.sort?.serverKey;
}

/**
 * The column a server `sort_by` value belongs to — the inverse of
 * {@link serverSortKey}. Index pages hydrate their sort indicator from the
 * server's `filters.sort_by`, which is a server key, not a column id.
 */
export function columnIdForServerSortKey<TRow>(descriptor: ResourceTableDescriptor<TRow>, sortKey: string | undefined): string | undefined {
    if (!sortKey) {
        return undefined;
    }

    return descriptor.columns.find((column) => column.sort?.serverKey === sortKey)?.id;
}

/**
 * "Quick filters: Status, Priority" or the all-records fallback — identical logic
 * at every call site that used to spell it out.
 */
export function quickFilterButtonLabel<TRow>(
    descriptor: ResourceTableDescriptor<TRow>,
    t: TranslateFn,
    columnFilters: Record<string, string[]>,
    scope: DerivationScope = {},
): string {
    const { quickFilterDefinitions } = buildToolbarDefinitions(descriptor, t, scope);
    const active = quickFilterDefinitions.filter((definition) => (columnFilters[definition.id] ?? []).length > 0).map((d) => d.label);

    return active.length > 0
        ? `${label(t, descriptor.quickFiltersLabel)}: ${active.join(', ')}`
        : label(t, descriptor.allLabel);
}

/**
 * Columns forced visible because a filter keyed to them is active.
 *
 * Filtering by a value you cannot see reads as a broken page — the calculation
 * queue could be narrowed by complexity and outcome while neither column was in
 * the default set.
 */
export function columnsRevealedByFilters<TRow>(
    descriptor: ResourceTableDescriptor<TRow>,
    columnFilters: Record<string, string[]>,
): string[] {
    const columnIds = new Set(descriptor.columns.map((column) => column.id));

    return Object.entries(columnFilters)
        .filter(([id, values]) => values.length > 0 && columnIds.has(id))
        .map(([id]) => id);
}
